import {action, makeObservable, observable} from 'mobx';
import {ApiRepository, ValidationError, ValidationErrorMultiple} from '../base/api-repository';
import {UserStore} from '../user/user-store';
import {LabelStore, Language} from '../base/label-store';
import {ViewStore} from '../base/view-store';
import {SessionStore} from '../user/session-store';
import {DateRange} from 'react-day-picker';
import {isValidLocalDate} from '../base/dateUtils';

export type CompanyPageTab = 'structure' | 'people' | 'matrix';
export type CompanyPageModal =
    'edit-employee'
    | 'add-employee'
    | 'add-department'
    | 'edit-department'
    | 'company-page-help'
    | 'edit-company-languages'
    | 'edit-company-settings'
    | 'edit-company-contacts'
    | 'add-matrix-team'
    | 'edit-matrix-team'
    | 'upload-employees'
    | 'wemply-config'
    | 'yester-config'
    | 'company-data-api-settings'
    | 'edit-targets'
    | 'api-key';

export class CompanyPageStore extends ViewStore {
    @observable
    payload?: CompanyPayload = undefined;
    @observable
    slackAuthPayload?: SlackAuthPayload = undefined;
    @observable
    variables: CompanyPageVariables = new CompanyPageVariables();

    constructor(
        private api: ApiRepository,
        private userStore: UserStore,
        private sessionStore: SessionStore,
        labelStore: LabelStore) {
        super(labelStore);
        this.variables.openTab = 'structure';
        makeObservable(this);
    }

    @action
    onEnter(): void {
        this.load(async () => {
            await this.loadPayload();
        });
    }

    @action
    onCompanyTabChange(context: CompanyPageTab, desiredDepartment?: string): void {
        this.variables.openTab = context;
        this.variables.searchKey = desiredDepartment?? '';
    }

    @action
    onSearchKeyChange(searchKey: string): void {
        this.variables.searchKey = searchKey;
    }

    @action
    openAddEmployeeModal(): void {
        this.variables.showValidation = false;
        this.variables.invalidEmail = false;
        this.variables.invalidPhone = false;
        this.variables.validationReason = undefined;
        this.variables.phoneAndEmailBothMissing = false;
        this.variables.employeeInEditing = undefined;
        this.variables.openModal = 'add-employee';
    }

    @action
    openEditEmployeeModal(employee: EmployeeDataInBusinessUnit): void {
        this.variables.showValidation = false;
        this.variables.invalidEmail = false;
        this.variables.invalidPhone = false;
        this.variables.validationReason = undefined;
        this.variables.phoneAndEmailBothMissing = false;
        this.variables.employeeInEditing = employee;
        this.variables.openModal = 'edit-employee';
    }

    @action
    openAddDepartmentModal(): void {
        this.variables.showValidation = false;
        this.variables.departmentInEditing = undefined;
        this.variables.validationReason = undefined;
        this.variables.openModal = 'add-department';
    }

    @action
    openEditDepartmentModal(oneBusinessUnit: OneBusinessUnit): void {
        this.variables.showValidation = false;
        this.variables.departmentInEditing = oneBusinessUnit;
        this.variables.validationReason = undefined;
        this.variables.openModal = 'edit-department';
    }

    @action
    openEditCompanySettingsModal(companyConfigurations: CompanySettingsPayload): void {
        this.variables.showValidation = false;
        this.variables.companyInEditing = companyConfigurations;
        this.variables.openModal = 'edit-company-settings';
    }

    @action
    openEditCompanyContactsModal(companyConfigurations: CompanySettingsPayload): void {
        this.variables.showValidation = false;
        this.variables.companyInEditing = companyConfigurations;
        this.variables.openModal = 'edit-company-contacts';
    }

    @action
    openEditCompanyLanguageModal(companyConfigurations: CompanySettingsPayload): void {
        this.variables.showValidation = false;
        this.variables.allLanguagesUnselected = false;
        this.variables.companyInEditing = companyConfigurations;
        this.variables.openModal = 'edit-company-languages';
    }

    @action
    openEditTargetsModal(companyConfigurations: CompanySettingsPayload): void {
        this.variables.companyInEditing = companyConfigurations;
        this.variables.openModal = 'edit-targets';
    }

    @action
    openWemplyModal(): void {
        this.variables.openModal = 'wemply-config';
    }

    @action
    openYesterModal(): void {
        this.variables.openModal = 'yester-config';
    }

    @action
    openCompanyDataApiSettingsModal(): void {
        this.variables.openModal = 'company-data-api-settings';
    }

    @action
    saveCompanyDataApiSettings(settings: CompanyDataApiSettingsToSave): void {
        this.refresh(async () => {
            await this.api.post(`/company/employee/${this.userStore.payload!.employeeId}/company-data-api-settings`, settings);
            this.closeModal();
            await this.loadPayload();
        });
    }

    @action
    async revokeCompanyDataApiSettings(): Promise<void> {
        await this.api.post(`/company/employee/${this.userStore.payload!.employeeId}/company-data-api-settings/revoke`);
        this.closeModal();
        await this.loadPayload();
    }

    @action
    openApiKeyModal(): void {
        this.variables.openModal = 'api-key';
    }

    @action
    async generateApiKey(): Promise<string | null> {
        try {
            const response = await this.api.post<{ apiKey: string }>(`/company/employee/${this.userStore.payload!.employeeId}/api-key/generate`);

            await this.loadPayload();

            return response.apiKey;
        } catch (e) {
            console.error('Failed to generate API key', e);

            return null;
        }
    }

    @action
    async revokeApiKey(): Promise<void> {
        await this.api.post(`/company/employee/${this.userStore.payload!.employeeId}/api-key/revoke`);
        this.closeModal();
        await this.loadPayload();
    }

    @action
    openCompanyPageHelpModal(): void {
        this.variables.openModal = 'company-page-help';
    }

    @action
    openUploadEmployeesModal(): void {
        this.variables.openModal = 'upload-employees';
    }

    @action
    openAddMatrixTeamModal(): void {
        this.variables.showValidation = false;
        this.variables.matrixTeamInEditing = undefined;
        this.variables.openModal = 'add-matrix-team';
    }

    @action
    openEditMatrixTeamModal(oneMatrixTeam: OneMatrixTeam): void {
        this.variables.showValidation = false;
        this.variables.matrixTeamInEditing = oneMatrixTeam;
        this.variables.openModal = 'edit-matrix-team';
    }

    @action
    closeModal(): void {
        this.variables.openModal = undefined;
    }

    @action
    saveEmployee(employee: EmployeeToSave): void {
        this.variables.invalidEmail = false;
        this.variables.invalidPhone = false;
        this.variables.phoneAndEmailBothMissing = false;
        this.refresh(async () => {
            if (this.isValidEmployee(employee)) {
                const cleanEmployee = {...employee, employeeTags: employee.employeeTags?.filter((value) => value !== '') };

                if (this.variables.openModal === 'add-employee') {
                    try {
                        await this.api.post(`/company/employee/${this.userStore.payload!.employeeId}/add-employee`, cleanEmployee);
                        this.closeModal();
                    } catch (e: unknown) {
                        this.catchInvalidDataInputError(e as Error);
                    }
                } else {
                    cleanEmployee.employeeId = this.variables.employeeInEditing?.employeeId;
                    try {
                        await this.api.post(`/company/employee/${this.userStore.payload!.employeeId}/edit-employee`, cleanEmployee);
                        this.closeModal();
                    } catch (e: unknown) {
                        this.catchInvalidDataInputError(e as Error);
                    }
                }
            } else {
                this.variables.showValidation = true;
            }
            await this.loadPayload();
        });
    }

    @action
    saveEmployeeExcel(excelFile: File | undefined): void {
        if (!excelFile) {
            return;
        }
        this.refresh(async () => {

            try {
                await this.api.postFile(`/company/employee/${this.userStore.payload!.employeeId}/upload-employees`, excelFile);
                this.closeModal();
            } catch (e: unknown) {
                this.catchExcelValidationErrors(e as Error);
            }
            await this.loadPayload();
        });
    }

    @action
    resetOnboarding(employee: EmployeeDataInBusinessUnit): void {
        this.refresh(async () => {
            if (window.confirm(this.label('company.people.onboarding-reset-confirm', {employee_name: employee.employeeFirstName + ' ' + employee.employeeLastName, onboardings_count: employee.createdOnboardingsCount.toString()}))) {
                const employeeToResetOnboarding: EmployeeToResetOnboarding = {
                    employeeId: employee.employeeId,
                };

                await this.api.post(`/company/employee/${this.userStore.payload!.employeeId}/reset-onboarding`, employeeToResetOnboarding);
                await this.loadPayload();
            }
        });
    }

    @action
    deleteEmployee(employee: EmployeeToDelete): void {
        if (window.confirm(this.label('company.people.delete-confirm'))) {
            this.refresh(async () => {
                try {
                    await this.api.post(`/company/employee/${this.userStore.payload!.employeeId}/delete-employee`, employee);
                } catch (e: unknown) {
                    if ((e as ValidationError).name === 'ValidationError') {
                        if ((e as ValidationError).errorMessageCode === 'remove-last-admin') {
                            window.alert(this.label('company.employee.delete-alert'));
                        }
                    } else {
                        throw e;
                    }
                }
                if (this.userStore.payload!.employeeId === employee.employeeId) {
                    window.location.assign('https://moticheck.com');
                    this.sessionStore.state = 'no-session';
                    localStorage.clear();
                } else {
                    await this.loadPayload();
                }
            });
        }
    }

    @action
    saveDepartment(businessUnit: BusinessUnitToSave): void {
        this.refresh(async () => {
            if (this.isValidBusinessUnit(businessUnit)) {
                if (this.variables.openModal === 'add-department') {
                    try {
                        await this.api.post(`/company/employee/${this.userStore.payload!.employeeId}/add-business-unit`, businessUnit);
                        this.closeModal();
                    } catch (e: unknown) {
                        this.catchInvalidDataInputError(e as Error);
                    }
                } else {
                    businessUnit.businessUnitId = this.variables.departmentInEditing?.id;
                    try {
                        await this.api.post(`/company/employee/${this.userStore.payload!.employeeId}/edit-business-unit`, businessUnit);
                        this.closeModal();
                    } catch (e: unknown) {
                        this.catchInvalidDataInputError(e as Error);
                    }
                }
            } else {
                this.variables.showValidation = true;
            }
            await this.loadPayload();
        });
    }

    @action
    deleteDepartment(oneBusinessUnit: BusinessUnitToDelete): void {
        if (window.confirm(this.label('company.department.delete-confirm'))) {
            this.refresh(async () => {
                await this.api.post(`/company/employee/${this.userStore.payload!.employeeId}/delete-business-unit`, oneBusinessUnit);
                await this.loadPayload();
            });
        }
    }

    @action
    async toggleBusinessUnit(businessUnitId: string, requestedIsActive: boolean, isActive: boolean): Promise<void> {
        const toggleBusinessUnit: ToggleBusinessUnit = {
            businessUnitId,
            requestedIsActive,
        };

        if (requestedIsActive !== isActive) {
            const confirmMessage = requestedIsActive
                ? this.label('company.business-unit.toggle-active-confirm')
                : this.label('company.business-unit.toggle-inactive-confirm');

            if (!window.confirm(confirmMessage)) {
                return;
            }
        }

        await this.api.post(`/company/employee/${this.userStore.payload!.employeeId}/toggle-business-unit`, toggleBusinessUnit);
        await this.loadPayload();
    }

    @action
    openStation(oneBusinessUnit: OneBusinessUnit): void {
        let stationUrl;

        if (window.location.hostname === 'localhost') {
            stationUrl = 'https://localhost:9000/station/' + oneBusinessUnit.id;
        } else if (window.location.hostname === 'test.moticheck.com') {
            stationUrl = 'https://test.moticheck.com/station/' + oneBusinessUnit.id;
        } else {
            stationUrl = 'https://app.moticheck.com/station/' + oneBusinessUnit.id;
        }
        window.open(stationUrl);
    }

    @action
    saveMatrixTeam(matrixTeam: MatrixTeamToSave): void {
        this.refresh(async () => {
            if (this.isValidMatrixTeam(matrixTeam)) {
                const cleanMatrixTeam = {...matrixTeam, childBusinessUnitIds: matrixTeam.childBusinessUnitIds?.filter((id) => id !== '') };

                if (this.variables.openModal === 'add-matrix-team') {
                    try {
                        await this.api.post(
                            `/company/employee/${this.userStore.payload!.employeeId}/add-matrix-team`,
                            cleanMatrixTeam,
                        );
                        this.closeModal();
                    } catch (e: unknown) {
                        this.catchInvalidDataInputError(e as Error);
                    }
                } else {
                    try {
                        cleanMatrixTeam.matrixTeamId = this.variables.matrixTeamInEditing?.matrixTeamId;
                        await this.api.post(`/company/employee/${this.userStore.payload!.employeeId}/edit-matrix-team`, cleanMatrixTeam);
                        this.closeModal();
                    } catch (e: unknown) {
                        this.catchInvalidDataInputError(e as Error);
                    }
                }
            } else {
                this.variables.showValidation = true;
            }
            await this.loadPayload();
        });
    }

    @action
    deleteMatrixTeam(matrixTeam: MatrixTeamToDelete): void {
        if (window.confirm(this.label('company.matrix-team.delete-confirm'))) {
            this.refresh(async () => {
                await this.api.post(`/company/employee/${this.userStore.payload!.employeeId}/delete-matrix-team`, matrixTeam);
                await this.loadPayload();
            });
        }
    }

    @action
    saveCompanyContacts(companySettingsToSave: CompanyContactsToSave): void {
        this.refresh(async () => {
            if (this.areValidCompanyContacts(companySettingsToSave)) {
                try {
                    await this.api.post(`/company/employee/${this.userStore.payload!.employeeId}/edit-company-contacts`, companySettingsToSave);
                    this.closeModal();
                    await this.userStore.refreshUser();
                    await this.loadPayload();
                } catch (e: unknown) {
                    this.catchInvalidDataInputError(e as Error);
                }
            } else {
                this.variables.showValidation = true;
            }
        });
    }

    @action
    saveCompanyTargets(target: SetTarget): void {
        this.refresh(async () => {
            await this.api.post(`/company/employee/${this.userStore.payload!.employeeId}/edit-target`, target);
            this.closeModal();
            await this.userStore.refreshUser();
            await this.loadPayload();
        });
    }

    @action
    saveWemplyConfig(wemplyConfig: WemplyIntegrationConfig): void {
        this.refresh(async () => {
            await this.api.post(`/company/employee/${this.userStore.payload!.employeeId}/wemply-config`, wemplyConfig);
            this.closeModal();
            await this.userStore.refreshUser();
            await this.loadPayload();
        });
    }

    @action
    async revokeWemplyConfig(): Promise<void> {
        await this.api.post(`/company/employee/${this.userStore.payload!.employeeId}/wemply-config/revoke`);
        this.closeModal();
        await this.userStore.refreshUser();
        await this.loadPayload();
    }

    @action
    saveYesterConfig(yesterConfig: YesterIntegrationConfig): void {
        this.refresh(async () => {
            await this.api.post(`/company/employee/${this.userStore.payload!.employeeId}/yester-config`, yesterConfig);
            this.closeModal();
            await this.userStore.refreshUser();
            await this.loadPayload();
        });
    }

    @action
    async revokeYesterConfig(): Promise<void> {
        await this.api.post(`/company/employee/${this.userStore.payload!.employeeId}/yester-config/revoke`);
        this.closeModal();
        await this.userStore.refreshUser();
        await this.loadPayload();
    }

    @action
    cleanupExcelErrors(): void {
        this.variables.excelValidationErrors = [];
        this.variables.showValidation = false;
        this.variables.validationReason = undefined;
    }

    @action
    saveCompanySettings(companySettingsToSave: CompanySettingsToSave): void {
        this.refresh(async () => {

            if (this.areValidCompanySettings(companySettingsToSave)) {
                try {
                    await this.api.post(`/company/employee/${this.userStore.payload!.employeeId}/edit-company-settings`, companySettingsToSave);
                    this.closeModal();
                    await this.userStore.refreshUser();
                    await this.loadPayload();
                } catch (e: unknown) {
                    this.catchInvalidDataInputError(e as Error);
                }
            } else {
                this.variables.showValidation = true;
            }
        });
    }

    @action
    saveCompanyLanguageSettings(companyLanguageSettings: CompanyLanguageSettingsToSave): void {
        this.refresh(async () => {

            if (this.checkLanguageSettings(companyLanguageSettings) === undefined) {
                try {
                    await this.api.post(`/company/employee/${this.userStore.payload!.employeeId}/edit-company-languages`, companyLanguageSettings);
                    this.closeModal();
                    await this.userStore.refreshUser();
                    await this.loadPayload();
                } catch (e: unknown) {
                    if ((e as ValidationError).name === 'ValidationError') {
                        if ((e as ValidationError).errorMessageCode === 'all-languages-unselected') {
                            this.variables.showValidation = true;
                        }
                    } else {
                        throw e;
                    }
                }
            } else {
                if (this.checkLanguageSettings(companyLanguageSettings) === 'all-languages-unselected') {
                    this.variables.showValidation = true;
                    this.variables.allLanguagesUnselected = true;
                } else {
                    this.variables.showValidation = true;
                }
            }
        });
    }

    @action
    deleteCompany(company: CompanyToDelete): void {
        if (window.confirm(this.label('company.company.delete-confirm'))) {
            this.refresh(async () => {
                await this.api.post(`/company/employee/${this.userStore.payload!.employeeId}/delete-confirmation`, company);
                window.location.assign('https://moticheck.com');
                this.sessionStore.state = 'no-session';
                localStorage.clear();
            });
        }
    }

    @action
    startSlackInstall(): void {
        let slackInstallUrl;

        if (window.location.hostname === 'localhost') {
            slackInstallUrl = 'https://slack.com/oauth/v2/authorize?client_id=1624995015185.3507874608823&scope=chat:write,chat:write.customize,im:read,im:write,users.profile:read,users:read.email,users:read&user_scope=users.profile:read,users:read.email,users:read&state=moticheck';
        } else if (window.location.hostname === 'test.moticheck.com') {
            slackInstallUrl = 'https://slack.com/oauth/v2/authorize?client_id=1624995015185.3515977258406&scope=chat:write.customize,chat:write,im:write,im:read,users:read.email,users:read,users.profile:read&user_scope=users.profile:read,users:read.email,users:read&state=moticheck';
        } else {
            slackInstallUrl = 'https://slack.com/oauth/v2/authorize?client_id=1624995015185.3519798408405&scope=users.profile:read,users:read.email,users:read,im:write,im:read,chat:write,chat:write.customize&user_scope=users:read.email,users:read,users.profile:read&state=moticheck';
        }
        window.open(slackInstallUrl);
    }

    @action
    onSlackAuthentication(code?: string, error?: string, state?: string): void {
        this.load(async () => {
            const codeQueryParam: Record<string, string> = {};
            const errorQueryParam: Record<string, string> = {};
            const stateQueryParam: Record<string, string> = {};

            if (code !== undefined) {
                codeQueryParam.code = code;
            }
            const codeQueryParamString = new URLSearchParams(codeQueryParam).toString();

            if (error !== undefined) {
                errorQueryParam.error = error;
                this.variables.slackAuthSuccess = false;
            }
            const errorQueryParamString = new URLSearchParams(errorQueryParam).toString();

            if (state !== undefined) {
                stateQueryParam.state = state;
            }
            const stateQueryParamString = new URLSearchParams(stateQueryParam).toString();

            this.slackAuthPayload = await this.api.get<SlackAuthPayload>(`/slack/oauth/completion?${codeQueryParamString}&${errorQueryParamString}&${stateQueryParamString}`);

            this.variables.slackAuthSuccess = this.slackAuthPayload.success;
        });
    }

    @action
    sendTeamsInstallInstructions(): void {
        if (window.confirm(this.label('company.teams.send-guidance-confirm'))) {
            this.refresh(async () => {
                await this.api.post(`/company/teams/${this.userStore.payload!.employeeId}/install-instructions`);
                await this.userStore.refreshUser();
                await this.loadPayload();
            });
        }
    }

    getEmployees(): EmployeeDataInBusinessUnit[] {
        if (this.variables.searchKey === '') {
            return this.payload!.allEmployeesPayload.allEmployees;
        } else {
            return this.payload!.allEmployeesPayload.allEmployees.filter((employee) => this.doesEmployeeMatchSearchKey(employee));
        }
    }

    employeeEligibleForDeletion(rootBusinessUnit: OneBusinessUnit, employeeDataInBusinessUnit: EmployeeDataInBusinessUnit): boolean {
        const employeeCount = rootBusinessUnit.activeEmployeeCount + rootBusinessUnit.inActiveEmployeeCount;

        return employeeCount > 1 && employeeDataInBusinessUnit.isLastAdmin !== true;
    }

    businessUnitEligibleForDeletion(oneBusinessUnit: OneBusinessUnit): boolean {
        const employeeCount = oneBusinessUnit.activeEmployeeCount + oneBusinessUnit.inActiveEmployeeCount;

        return oneBusinessUnit.children.length < 1 && employeeCount < 1;
    }

    doesEmployeeMatchSearchKey(employee: EmployeeDataInBusinessUnit): boolean {
        if (this.doesMatchWithSearchKey(employee.employeeFirstName)) {
            return true;
        }

        if (this.doesMatchWithSearchKey(employee.employeeLastName)) {
            return true;
        }

        if (this.doesMatchWithSearchKey(employee.businessUnitName)) {
            return true;
        }

        if (this.doesMatchWithSearchKey(employee.parentBusinessUnitName)) {
            return true;
        }

        if (this.doesMatchWithSearchKey(employee.position)) {
            return true;
        }

        if (this.doesMatchWithSearchKey(this.label(`common.location.${employee.locationId}`))) {
            return true;
        }

        let role = '';

        if (employee.isAdmin === true) {
            role = 'admin';
        } else if (employee.isManager === true) {
            role = 'manager';
        } else if (employee.isPartner === true) {
            role = 'employee-partner';
        } else {
            role = 'employee';
        }

        return this.doesMatchWithSearchKey(this.label(`company.people.rights.${role}`));

    }

    doesMatchWithSearchKey(value?: string): boolean {
        if (value === undefined) {
            return false;
        }

        return value.toLowerCase().indexOf(this.variables.searchKey.toLowerCase()) !== -1;

    }

    isValidEmployee(employee: EmployeeToSave): boolean {
        if (employee.email !== undefined && employee.phoneNumber !== undefined) {
            return this.hasValidEmployeeEntries(employee)
                && !this.checkEmail(employee.email)
                && !this.checkPhoneNumber(employee.phoneNumber);
        } else if (employee.email !== undefined && employee.phoneNumber === undefined) {
            return this.hasValidEmployeeEntries(employee)
                && !this.checkEmail(employee.email);
        } else if (employee.email === undefined && employee.phoneNumber !== undefined) {
            return this.hasValidEmployeeEntries(employee)
                && !this.checkPhoneNumber(employee.phoneNumber);
        } else {
            return this.hasValidEmployeeEntries(employee) && this.hasPhoneOrEmailFilled(employee);
        }
    }

    hasValidEmployeeEntries(employee: EmployeeToSave): boolean {
        let hasValidEntries: boolean;

        if (employee.startDate !== undefined) {
            hasValidEntries = !this.checkIfMissing(employee.firstName) && !this.checkIfMissing(employee.lastName) && !this.checkStartDate(employee.startDate);
        } else {
            hasValidEntries = !this.checkIfMissing(employee.firstName) && !this.checkIfMissing(employee.lastName);
        }

        return hasValidEntries;
    }

    hasPhoneOrEmailFilled(employee: EmployeeToSave): boolean {
        if (employee.email === undefined || employee.phoneNumber === undefined) {
            this.variables.phoneAndEmailBothMissing = true;

            return false;
        } else {
            return true;
        }
    }

    isValidBusinessUnit(businessUnit: BusinessUnitToSave): boolean {
        return !this.checkIfMissing(businessUnit.businessUnitName);
    }

    isValidMatrixTeam(matrixTeam: MatrixTeamToSave): boolean {
        return !this.checkIfMissing(matrixTeam.matrixTeamName);
    }

    areValidCompanySettings(company: CompanySettingsToSave): boolean {
        return !this.checkIfMissing(company.emailName)
            && !this.checkIfAnswerContextIsMissing(company.answerContext)
            && !this.checkIfChannelsAreMissing(company.allowedChannels);
    }

    areValidCompanyContacts(company: CompanyContactsToSave): boolean {
        return !this.checkIfMissing(company.name)
            && !this.checkIfDateIsMissing(company.surveyStartDate)
            && !this.checkIfMissing(company.address)
            && !this.checkIfMissing(company.regNumber);
    }

    checkLanguageSettings(companyLanguages: CompanyLanguageSettingsToSave): undefined | 'all-languages-unselected' | 'default-language-unselected' {
        if (this.allLanguagesUnselected(companyLanguages) !== undefined) {
            return 'all-languages-unselected';
        } else if (this.defaultLanguageUnselected(companyLanguages) !== undefined) {
            return 'default-language-unselected';
        } else {
            return undefined;
        }
    }

    allLanguagesUnselected(companyLanguages: CompanyLanguageSettingsToSave): undefined | 'all-languages-unselected' {
        if (!companyLanguages.hasEnglish
            && !companyLanguages.hasEstonian
            && !companyLanguages.hasFinnish
            && !companyLanguages.hasLatvian
            && !companyLanguages.hasLithuanian
            && !companyLanguages.hasPolish
            && !companyLanguages.hasSwedish
            && !companyLanguages.hasVietnamese
            && !companyLanguages.hasJapanese
            && !companyLanguages.hasRussian) {
            return 'all-languages-unselected';
        } else {
            return undefined;
        }
    }

    defaultLanguageUnselected(companyLanguages: CompanyLanguageSettingsToSave): undefined | 'default-language-unselected' {
        if (companyLanguages.defaultLanguage === 'en' && !companyLanguages.hasEnglish) {
            return 'default-language-unselected';
        } else if (companyLanguages.defaultLanguage === 'et' && !companyLanguages.hasEstonian) {
            return 'default-language-unselected';
        } else if (companyLanguages.defaultLanguage === 'fi' && !companyLanguages.hasFinnish) {
            return 'default-language-unselected';
        } else if (companyLanguages.defaultLanguage === 'lv' && !companyLanguages.hasLatvian) {
            return 'default-language-unselected';
        } else if (companyLanguages.defaultLanguage === 'lt' && !companyLanguages.hasLithuanian) {
            return 'default-language-unselected';
        } else if (companyLanguages.defaultLanguage === 'pl' && !companyLanguages.hasPolish) {
            return 'default-language-unselected';
        } else if (companyLanguages.defaultLanguage === 'sv' && !companyLanguages.hasSwedish) {
            return 'default-language-unselected';
        } else if (companyLanguages.defaultLanguage === 'ru' && !companyLanguages.hasRussian) {
            return 'default-language-unselected';
        } else if (companyLanguages.defaultLanguage === 'vi' && !companyLanguages.hasVietnamese) {
            return 'default-language-unselected';
        } else if (companyLanguages.defaultLanguage === 'ja' && !companyLanguages.hasJapanese) {
            return 'default-language-unselected';
        } else {
            return undefined;
        }
    }

    checkIfChannelsAreMissing(value: string[] | null): 'missing-channels' |undefined {
        const anyChannelSelected = value?.includes('email') || value?.includes('slack') || value?.includes('mobile') || value?.includes('teams');

        if (value !== null && value.length && value !== undefined && anyChannelSelected) {
            return undefined;
        } else {
            return 'missing-channels';
        }
    }

    checkIfAnswerContextIsMissing(value: string[] | null): 'missing-context' |undefined {
        const anySelected = value?.includes('BINARY') || value?.includes('FOUR_OPTIONS') || value?.includes('FIVE_OPTIONS') || value?.includes('ONE_TO_FIVE_OPTIONS');

        if (value !== null && value.length && value !== undefined && anySelected) {
            return undefined;
        } else {
            return 'missing-context';
        }
    }

    checkIfMissing(value: string): undefined | 'missing' {
        if (value === '') {
            return 'missing';
        } else {
            return undefined;
        }
    }

    getTagNameError(firstError: string, secondError: string): string | undefined {
        if (this.variables.validationReason === firstError) {
            return 'forbidden-characters';
        }

        if (this.variables.validationReason === secondError) {
            return secondError;
        }

        return undefined;
    }

    getNameError(name: string, error: string): string | undefined {
        if (this.variables.validationReason === error) {
            return 'forbidden-characters';
        }

        return this.checkIfMissing(name);
    }

    checkIfDateIsMissing(value: Date | string | DateRange | undefined): undefined | 'missing' {
        if (value === undefined) {
            return 'missing';
        } else {
            return undefined;
        }
    }

    checkEmail(value: string): undefined | 'invalid-email-format' | 'duplicate-email' | 'missing-one-or-another' {
        const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+[^\s@]+$/;

        if (this.variables.phoneAndEmailBothMissing) {
            return 'missing-one-or-another';
        } else if (!validEmail.test(value)) {
            return 'invalid-email-format';
        } else if (this.variables.invalidEmail) {
            return 'duplicate-email';
        } else {
            return undefined;
        }
    }

    checkPhoneNumber(value: string): undefined | 'invalid-phone-format' | 'duplicate-phone-number' {
        const validPhoneNumber = /^\+\d{8,20}$/;

        if (!validPhoneNumber.test(value)) {
            return 'invalid-phone-format';
        } else if (this.variables.invalidPhone) {
            return 'duplicate-phone-number';
        } else {
            return undefined;
        }
    }

    checkStartDate(value: string): 'invalid-format' | undefined {
        if (!isValidLocalDate(value)) {
            return 'invalid-format';
        } else {
            return undefined;
        }
    }

    getEligibleParentBusinessUnits(editedBusinessUnit: OneBusinessUnit): OneBusinessUnit[] {
        return this.getBusinessUnits().filter((businessUnit) => !this.flattenBusinessUnit(editedBusinessUnit).includes(businessUnit));
    }

    getChildBusinessUnitIds(oneMatrixTeam: OneMatrixTeam): string[] {
        const teamIds: string[] = [];

        if (oneMatrixTeam.children !== null && oneMatrixTeam.children.length > 0) {
            oneMatrixTeam.children.forEach((child) => teamIds.push(child.id!));
        } else {
            teamIds.push('');
        }

        return teamIds;
    }

    getBusinessUnits(): OneBusinessUnit[] {
        return this.flattenBusinessUnit(this.payload!.rootOneBusinessUnit);
    }

    private flattenBusinessUnit(businessUnit: OneBusinessUnit): OneBusinessUnit[] {
        let businessUnits: OneBusinessUnit[] = [businessUnit];

        businessUnit.children.forEach((child): void => {
            businessUnits = businessUnits.concat(this.flattenBusinessUnit(child));
        });

        return businessUnits;
    }

    private async loadPayload(): Promise<void> {
        this.payload = await this.api.get<CompanyPayload>(`/company/employee/${this.userStore.payload!.employeeId}`);
    }

    private catchInvalidDataInputError(e: Error): void {
        if (e.name === 'ValidationError') {
            this.variables.showValidation = true;
            if ((e as ValidationError).errorMessageCode === 'duplicate_phone') {
                this.variables.invalidPhone = true;
            }
            if ((e as ValidationError).errorMessageCode === 'duplicate_email') {
                this.variables.invalidEmail = true;
            }
            this.variables.showValidation = true;
            this.variables.validationReason = (e as ValidationError).errorMessageCode;
        } else {
            this.closeModal();
            throw e;
        }
    }

    private catchExcelValidationErrors(e: Error): void {
        if (e.name === 'ValidationError') {
            this.variables.showValidation = true;
            this.variables.validationReason = (e as ValidationError).errorMessageCode;
        } else if (e.name === 'ValidationErrorMultiple') {
            this.variables.excelValidationErrors = (e as ValidationErrorMultiple).errorMessageCodes;
        } else {
            this.closeModal();
            throw e;
        }
    }
}

interface CompanyPayload {
    rootOneBusinessUnit: OneBusinessUnit
    allEmployeesPayload: AllEmployeesPayload
    matrixTeamsPayload: OneMatrixTeam[]
    allLocations: string[]
    companySettingsPayload: CompanySettingsPayload
    allTags?: string[],
    target: SetTarget,
    wemplyConfig?: WemplyConfigMasked,
    yesterConfig?: YesterConfigMasked,
    companyDataApiSettings?: CompanyDataApiSettingsMasked,
    apiKey?: ApiKeyMasked
}

export interface CompanyDataApiSettingsMasked {
    companyId: string;
    tenantId: string;
    clientApplicationIds: string[] | Set<string>;
    enabled: boolean;
    updatedAt: string;
    updatedBy: string;
}

export interface CompanyDataApiSettingsToSave {
    companyId?: string;
    tenantId: string;
    clientApplicationIds: string[];
    enabled: boolean;
}

export interface ApiKeyMasked {
    maskedKey: string;
    createdByName: string;
    createdAt: string;
}

export interface OneBusinessUnit {
    id: string,
    activeEmployeeCount: number,
    inActiveEmployeeCount: number,
    businessUnitName: string,
    managerName?: string,
    managerId?: string,
    isActive: boolean,
    parentBusinessUnitId?: string,
    children: OneBusinessUnit[],
    requestedIsActive?: boolean;
    partners: string[]
}

export interface BusinessUnitToSave {
    parentBusinessUnitId?: string,
    businessUnitName: string,
    businessUnitId?: string,
    managerId?: string,
    partners?: string[] | null,
}

export interface ToggleBusinessUnit{
    businessUnitId: string,
    requestedIsActive: boolean
}

export interface BusinessUnit {
    id: string,
    externalId: string
    name: string,
    parentBusinessUnitId: string
    companyId: string,
    isActive: boolean,
}

export interface BusinessUnitToDelete {
    id: string,
}

export interface OneMatrixTeam {
    matrixTeamId: string,
    matrixTeamName: string,
    managerName?: string,
    managerId?: string,
    children: OneBusinessUnit[],
    activeEmployeeCount?: number,
    inActiveEmployeeCount?: number,
}

export interface MatrixTeamToSave {
    matrixTeamName: string,
    matrixTeamId?: string,
    childBusinessUnitIds?: string[] | null,
    managerId?: string
}

export interface MatrixTeamToDelete {
    matrixTeamId: string,
}

export interface EmployeeDataInBusinessUnit {
    employeeId: string,
    employeeFirstName: string,
    employeeLastName: string,
    email?: string,
    phoneNumber?: string,
    position: string,
    locationId: string,
    startDate: string,
    businessUnitName: string,
    businessUnitId: string,
    parentBusinessUnitName?: string,
    isManager?: boolean,
    isPartner?: boolean,
    isAdmin?: boolean,
    isLastAdmin?: boolean,
    isOnboarded?: boolean,
    createdOnboardingsCount: number,
    employeeTags: string[],
    partnerRightsForBusinessUnits: string[],
    wemplyId: string,
}

export interface EmployeeToSave {
    firstName: string,
    lastName: string,
    email?: string,
    phoneNumber?: string,
    businessUnitId: string,
    position?: string,
    locationId: string,
    startDate?: string,
    employeeId?: string,
    isManager?: boolean,
    isAdmin?: boolean,
    employeeTags?: string[] | null,
    newTag?: string,
    partnerRightsForBusinessUnits?: string[] | null,
    wemplyId?: string,
}

export interface EmployeeToResetOnboarding {
    employeeId: string,
}

export interface EmployeeToDelete {
    employeeId: string,
}

export interface AllEmployeesPayload {
    allEmployees: EmployeeDataInBusinessUnit[]
}

export interface CompanySettingsPayload {
    name: string,
    companyId: string,
    address: string,
    regNumber: string,
    surveyStartDate: string,
    defaultLanguage: string,
    emailName: string,
    isMentorEnabled: boolean,
    isPersonalDashboardEnabled: boolean;
    isPublicSector: boolean,
    dataHiddenFromEmployees: DataHiddenFromEmployee;
    questionsFrequency: string,
    companyAvailableLanguages: Language[],
    questionsTimezone: string,
    questionsLogic: string,
    allowedChannels: string[],
    answerContext: string[],
    logoFile: string;
    isBusinessUnitDisablingEnabled: boolean,
    target: SetTarget,
    timezoneId: string[],
    wemplyConfig?: WemplyIntegrationConfig,
    yesterConfig?: YesterIntegrationConfig
}

export interface IntegrationConfig {
    companyId?: string;
    authKey?: string;
    syncAbsences: boolean;
    syncEmployeeData: boolean;
}

export type WemplyIntegrationConfig = IntegrationConfig;
export type YesterIntegrationConfig = IntegrationConfig;

export interface IntegrationConfigMasked {
    companyId?: string;
    maskedAuthKey: string;
    syncAbsences: boolean;
    syncEmployeeData: boolean;
}

export type WemplyConfigMasked = IntegrationConfigMasked;
export type YesterConfigMasked = IntegrationConfigMasked;

export interface SetTarget {
    activityRate: string,
    resultRate: string
}

export interface SlackAuthPayload {
    success: boolean
}

export interface CompanyContactsToSave {
    companyId: string,
    name: string,
    address: string,
    regNumber: string,
    surveyStartDate: string,
    questionsTimezone: string,
}

export interface CompanySettingsToSave {
    companyId: string,
    emailName: string,
    isMentorEnabled: boolean,
    isPersonalDashboardEnabled: boolean;
    isPublicSector: boolean,
    dataHiddenFromEmployees: DataHiddenFromEmployee;
    questionsFrequency: string,
    questionsLogic: string,
    allowedChannels: string[] | null,
    answerContext: string[] | null,
    logoFile: string | ArrayBuffer | null,
    isBusinessUnitDisablingEnabled: boolean
}

export interface DataHiddenFromEmployee {
    employeesCannotSeeComments: boolean,
    employeesCannotSeeTagsCard: boolean,
    employeesCannotSeeLocationsCard: boolean,
    employeesCannotSeeRolesCard: boolean,
    employeesCannotSeeServiceLengthsCard: boolean,
    employeesCannotSeeCategoriesCard: boolean,
    employeesCannotSeeDepartmentsCard: boolean,
}

export interface CompanyLanguageSettingsToSave {
    companyId: string,
    defaultLanguage: string,
    hasEnglish: boolean,
    hasEstonian: boolean,
    hasFinnish: boolean,
    hasLatvian: boolean,
    hasLithuanian: boolean,
    hasPolish: boolean,
    hasSwedish: boolean,
    hasRussian: boolean,
    hasJapanese: boolean,
    hasVietnamese: boolean,
}

export interface CompanyToDelete {
    companyId: string,
}

class CompanyPageVariables {
    @observable
    searchKey: string = '';
    @observable
    openTab?: CompanyPageTab = undefined;
    @observable
    openModal?: CompanyPageModal = undefined;
    @observable
    employeeInEditing?: EmployeeDataInBusinessUnit = undefined;
    @observable
    departmentInEditing?: OneBusinessUnit = undefined;
    @observable
    matrixTeamInEditing?: OneMatrixTeam = undefined;
    @observable
    companyInEditing?: CompanySettingsPayload = undefined;
    @observable
    showValidation?: boolean = false;
    @observable
    validationReason?: string = undefined;
    @observable
    invalidEmail?: boolean = false;
    @observable
    invalidPhone?: boolean = false;
    @observable
    phoneAndEmailBothMissing?: boolean = false;
    @observable
    allLanguagesUnselected?:boolean = false;
    @observable
    slackAuthSuccess?: boolean = false;
    @observable
    excelValidationErrors: string[] = [];


    constructor() {
        makeObservable(this);
    }
}
